import { useMemo, useState } from 'react';
import { useSetError } from '../../../redux/modals';
import {
  StreamingOptimizationsLambdaFetch,
  awaitJSON,
  getSafeErrorMessage
} from '../../../utils/fetch-utils';
import { prepareSavePayload } from './post-optimization-data-transform';
import { PostOptimizationEditState } from './usePostOptimizationEdits';
import {
  PlacementAnalyticsRow,
  PostOptimizationResponse,
  PostOptimizationSavePayload,
  TopLevelRow
} from './post-optimization-constants';

interface UseSaveResetParams {
  allocationCriteria: keyof PlacementAnalyticsRow;
  runId: string;
  company: string;
  streamingType: string;
  apiData: PostOptimizationResponse;
  resetChanges: () => void;
}

interface UseSaveResetReturn {
  handleSave: (currentData: TopLevelRow[]) => Promise<void>;
  handleReset: () => Promise<void>;
  isSaving: boolean;
  isResetting: boolean;
  originalEdits: Map<string, PostOptimizationEditState>;
}

/**
 * Custom hook for managing save and reset operations
 * Handles API calls, loading states, and error handling
 */
export const useSaveReset = ({
  allocationCriteria,
  runId,
  company,
  streamingType,
  apiData,
  resetChanges
}: UseSaveResetParams): UseSaveResetReturn => {
  const [isSaving, setIsSaving] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const setError = useSetError();
  const [lastSavedEdits, setLastSavedEdits] = useState<PostOptimizationSavePayload | null>(null);

  /**
   * Persist edits extracted from `data` to S3. On success, the saved payload becomes
   * the new baseline and unsaved user edits are cleared.
   */
  const persistEdits = async (
    data: TopLevelRow[],
    setBusy: (busy: boolean) => void,
    errorTitle: string
  ) => {
    setBusy(true);
    try {
      const payload = prepareSavePayload(data, allocationCriteria, runId, company, streamingType);

      const response = await StreamingOptimizationsLambdaFetch('/save_post_optimization_edits', {
        method: 'POST',
        body: payload
      });

      const result = await awaitJSON(response);
      if (result.error) {
        throw new Error(result.error);
      }

      setLastSavedEdits(payload);
      resetChanges();
    } catch (error) {
      setError({
        title: errorTitle,
        message: getSafeErrorMessage(error, 'Please try again.'),
        reportError: error
      });
    } finally {
      setBusy(false);
    }
  };

  /** Save current allocations and edits (takes data as parameter to avoid stale closures) */
  const handleSave = (calculatedData: TopLevelRow[]) =>
    persistEdits(calculatedData, setIsSaving, "Couldn't save");

  /** Clear all edits and persist the empty state */
  const handleReset = () => persistEdits([], setIsResetting, "Couldn't reset");

  /**
   * Get the original edits from either last saved state or API data
   */
  const originalEdits = useMemo(() => {
    const map = new Map<string, PostOptimizationEditState>();
    const source = lastSavedEdits || apiData.savedEdits;
    if (source) {
      source.placement_edits.forEach(edit => {
        map.set(edit.placement_id, {
          mustInclude: edit.must_include,
          mustExclude: edit.must_exclude,
          shouldReallocateBudget: edit.should_reallocate_budget
        });
      });
    }
    return map;
  }, [apiData.savedEdits, lastSavedEdits]);

  return {
    handleSave,
    handleReset,
    isSaving,
    isResetting,
    originalEdits
  };
};
